import { GenericObject, Nullable } from '@dxb-web-ui-framework/common/types';
import { isEmpty } from '@dxb-web-ui-framework/common/utils/Helper';
import { useContext, useContextSelector } from 'use-context-selector';
import { getContextPreloadedDataByProperties } from '../../utils/getContextPreloadedDataByProperties';
import { ClientSideWizardContext } from './ClientSideWizardContext';
import { ClientSideWizardCurrentProcessState } from './types';

const useWizardState = () => {
  const context = useContext(ClientSideWizardContext);

  if (context === undefined) {
    throw new Error('useWizardState must be used within WizardProvider');
  }

  return context;
};

const useWizardCurrentProcessState = () => {
  const context = useContextSelector(ClientSideWizardContext, state => state.currentProcessState);
  if (context === undefined) {
    throw new Error('useWizardCurrentProcessState must be used within WizardProvider');
  }

  return context;
};
/**
 *
 * @param {String | String[] | GenericObject} stepId optionally, the given step's id which the form data we want from.
 * If we provide the hook with an array of step ids, then it returns the corresponding steps' data.
 * @returns if given a step id, it returns the step's form data, otherwise it returns the whole process state
 */
const useWizardStepData = (
  stepId?: string | string[] | GenericObject,
): ClientSideWizardCurrentProcessState | GenericObject => {
  let context = useContextSelector(ClientSideWizardContext, state => state.currentProcessState);
  if (typeof stepId !== 'string' && stepId?.values) {
    return stepId;
  }

  if (stepId && typeof stepId === 'string') {
    context = context.formData?.data[stepId];
  }

  if (stepId && Array.isArray(stepId)) {
    let _context = {} as GenericObject;
    stepId.forEach(step => {
      _context[step] = { values: {}, stepId: step };
      _context[step].values = context.formData?.data[step].values;
    });
    context = _context as ClientSideWizardCurrentProcessState;
  }

  if (context === undefined) {
    throw new Error('useWizardStepData must be used within WizardProvider');
  }
  return context;
};

const useWizardErrorState = () => {
  let context = useContextSelector(ClientSideWizardContext, state => state.errorState);
  if (context === undefined) {
    throw new Error('useWizardCurrentProcessState must be used within WizardProvider');
  }
  return context;
};

const useWizardPreloadedData = (properties?: string | string[]): Nullable<GenericObject | string | number | null> => {
  const context = useContextSelector(ClientSideWizardContext, state => state.preloadedData);

  if (context === undefined) {
    throw new Error('useWizardPreloadedData must be used within WizardProvider');
  }

  if (context === null) {
    return context;
  }

  if (Array.isArray(properties) && !isEmpty(properties)) {
    return getContextPreloadedDataByProperties(properties, context);
  }

  if (properties) {
    return context?.values?.[properties as string];
  }

  return context;
};

const useWizardGoToNextStep = () => {
  const context = useContextSelector(ClientSideWizardContext, state => state.goToNextWizardStep);

  if (context === undefined) {
    throw new Error('useWizardGoToNextStep must be used within WizardProvider');
  }

  return context;
};

const useWizardGoToPreviousStep = () => {
  const context = useContextSelector(ClientSideWizardContext, state => state.goToPreviousWizardStep);

  if (context === undefined) {
    throw new Error('useWizardGoToPreviousStep must be used within WizardProvider');
  }

  return context;
};

const useWizardTotalSteps = () => {
  const context = useContextSelector(ClientSideWizardContext, state => state.totalSteps);

  if (context === undefined) {
    throw new Error('useWizardTotalSteps must be used within WizardProvider');
  }

  return context;
};

const useWizardStepsDone = () => {
  const context = useContextSelector(ClientSideWizardContext, state => state.completedSteps);

  if (context === undefined) {
    throw new Error('useWizardStepsDone must be used within WizardProvider');
  }

  return context;
};

const useWizardCurrentStepConfiguration = () => {
  const context = useContextSelector(ClientSideWizardContext, state => state.currentProcessState.currentStepConfig);

  if (context === undefined) {
    throw new Error('useWizardCurrentStepConfiguration must be used within WizardProvider');
  }

  return context;
};

const useWizardHasStepIndicator = () => {
  const context = useContextSelector(ClientSideWizardContext, state => state.hasStepIndicator);

  if (context === undefined) {
    throw new Error('useWizardHasStepIndicator must be used within WizardProvider');
  }

  return context;
};

const useWizardStepsConfig = () => {
  const context = useContextSelector(ClientSideWizardContext, state => state.stepsConfig);

  if (context === undefined) {
    throw new Error('useWizardStepsConfig must be used within WizardProvider');
  }

  return context;
};

export {
  useWizardCurrentProcessState,
  useWizardPreloadedData,
  useWizardErrorState,
  useWizardCurrentStepConfiguration,
  useWizardState,
  useWizardGoToNextStep,
  useWizardGoToPreviousStep,
  useWizardTotalSteps,
  useWizardStepsDone,
  useWizardHasStepIndicator,
  useWizardStepData,
  useWizardStepsConfig,
};
